import React, { useState } from 'react';
import useAdminReviewList from './useAdminReviewList';
import { getPendingCreatorVerifications, approveCreator, rejectCreator } from '../../services/api';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../auth/AuthContext';
import {
  CheckCircle,
  XCircle,
  UserCheck,
  Mail,
  Instagram,
  Globe,
  Search,
  X,
  ExternalLink,
  Users,
  Smartphone
} from 'lucide-react';
import AdminPageHeader from './AdminPageHeader';
import ProtectedDocument from '../common/ProtectedDocument';
import './AdminStyles.scss';



const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 15 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.4, ease: [0.2, 0.8, 0.2, 1] }
  }
};

const MetricCard = ({ icon: Icon, value, label, iconClass = "dashboard-view__metric-icon-wrap--neutral" }) => (
  <motion.div className="dashboard-view__metric-card" variants={itemVariants}>
    <div className="dashboard-view__metric-label">{label}</div>
    <div className="dashboard-view__metric-row">
        <span className="dashboard-view__metric-value">{value}</span>
    </div>
    <div className="dashboard-view__metric-content" style={{ marginTop: '12px' }}>
        <div className={`dashboard-view__metric-icon-wrap ${iconClass}`}>
            <Icon className="dashboard-view__metric-icon" />
        </div>
    </div>
  </motion.div>
);


const CreatorVerifications = () => {
  const [error, setError] = useState(null);
  const [selectedCreator, setSelectedCreator] = useState(null);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [processing, setProcessing] = useState(false);
  const [success, setSuccess] = useState(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [filterCountry, setFilterCountry] = useState('');
  const { currentUser } = useAuth();
  const canReview = Boolean(currentUser?.is_superuser);

  const { results: pendingVerifications, loading, error: loadError, page, setPage, pagination, summary, countries, refresh } = useAdminReviewList(getPendingCreatorVerifications, { search: searchTerm, country: filterCountry });
  const filteredVerifications = pendingVerifications;

  const handleApprove = async (creatorId) => {
    setProcessing(true);
    try {
      await approveCreator(creatorId);
      refresh();
      setSuccess('Creador aprobado.');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError('Error al aprobar');
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async (e) => {
    e.preventDefault();
    setProcessing(true);
    try {
      await rejectCreator(selectedCreator.id, { reason: rejectionReason });
      refresh();
      setShowRejectModal(false);
      setRejectionReason('');
      setSuccess('Solicitud rechazada.');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError('Error al rechazar');
    } finally {
      setProcessing(false);
    }
  };

  if (loading) return <div className="dashboard-loading"><div className="spinner" /></div>;

  return (
    <motion.div className="dashboard-view__inner" variants={containerVariants} initial="hidden" animate="visible">
        <AdminPageHeader 
          title="Verificaciones de Creadores" 
          subtitle="Audita y categoriza a los creadores de contenido que desean unirse a FOTH."
          breadcrumbs="Creadores" 
        />

        <div className="dashboard-view__grid">
            <div className="dashboard-view__metric-grid">
               <MetricCard icon={Users} label="PENDIENTES" value={summary.total || 0} iconClass="dashboard-view__metric-icon-wrap--gold" />
               <MetricCard icon={Globe} label="PAÍSES" value={summary.countries || 0} iconClass="dashboard-view__metric-icon-wrap--neutral" />
            </div>
        </div>

        <div className="dashboard-view__panel">
            <div className="luxury-filters">
                <div className="search-box-modern">
                    <Search size={18} />
                    <input 
                        type="text" 
                        placeholder="Buscar por nombre, email o IG..." 
                        className="admin-input-minimal"
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                    />
                </div>
                <div className="filter-actions">
                    <select className="admin-select-minimal" value={filterCountry} onChange={e => setFilterCountry(e.target.value)}>
                        <option value="">Todos los países</option>
                        {countries.map(({ id, name }) => <option key={id} value={id}>{name}</option>)}
                    </select>
                </div>
            </div>

            <AnimatePresence>
                {success && <motion.div className="alert-luxury alert-luxury--success" role="status" initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}}>{success}</motion.div>}
                {(error || loadError) && <motion.div className="alert-luxury alert-luxury--danger" role="alert" initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}}>{error || loadError}</motion.div>}
            </AnimatePresence>

            <div className="verifications-stack">
                {filteredVerifications.length === 0 ? (
                    <div className="empty-state-luxury">
                        <UserCheck size={40} opacity={0.2} />
                        <h3>No hay creadores pendientes</h3>
                    </div>
                ) : (
                    filteredVerifications.map(creator => (
                        <motion.div key={creator.id} className="verification-item-lux" variants={itemVariants}>
                            <div className="item-header">
                                <div className="biz-info">
                                    <div className="avatar-box" style={{ background: '#d4b24c' }}><UserCheck size={20}/></div>
                                    <div className="text-box">
                                        <h4>{creator.user.username}</h4>
                                        <span>Inscrito: {new Date(creator.user.date_joined).toLocaleDateString()}</span>
                                    </div>
                                </div>
                                 <div className="location-tag">
                                    <Instagram size={14} /> @{creator.ig_account || 'n/a'}
                                </div>
                            </div>

                            <div className="details-grid">
                                <div className="detail-col">
                                    <label>Contacto & Redes</label>
                                    <p><Mail size={12}/> {creator.user.email}</p>
                                    {creator.instagram_account_connected ? (
                                        <div className="ig-admin-status">
                                            <a 
                                                href={`https://www.instagram.com/${creator.ig_account}/`} 
                                                target="_blank" 
                                                rel="noopener noreferrer"
                                                className="ig-handle-link"
                                            >
                                                <Instagram size={12} color="#E1306C" /> 
                                                <strong>@{creator.ig_account}</strong>
                                                <ExternalLink size={10} style={{ opacity: 0.5 }} />
                                            </a>
                                            <p className="ig-followers">
                                                {creator.ig_followers?.toLocaleString() || 0} seguidores
                                            </p>
                                        </div>
                                    ) : (
                                        <p className="status-badge-soft-danger">No conectado</p>
                                    )}
                                </div>
                                <div className="detail-col">
                                    <label>Ubicación</label>
                                    <p><Globe size={12}/> {creator.user.country_name || 'País no especificado'}</p>
                                    <p><Smartphone size={12}/> {creator.user.phone_number || 'S/N'}</p>
                                </div>
                            </div>

                            <ProtectedDocument url={creator.selfie_url} title="Selfie de verificación" />
                            {/* Documents panel */}
                            {creator.id_document_url && (
                                <div className="verification-docs-panel mt-4">
                                    <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 block">
                                        Documento de Identidad
                                    </label>
                                    <div className="doc-preview-luxury">
                                        <ProtectedDocument url={creator.id_document_url} title="Documento de identidad" />
                                    </div>
                                </div>
                            )}

                            <div className="action-row">
                                {canReview && <button className="admin-btn admin-btn--primary" onClick={() => handleApprove(creator.id)} disabled={processing}>
                                    <CheckCircle size={16} /> Validar Creador
                                </button>}
                                {canReview && <button className="admin-btn admin-btn--outline" style={{ color: '#ff4d5e', borderColor: 'rgba(255,77,94,0.2)' }} onClick={() => { setSelectedCreator(creator); setShowRejectModal(true); }}>
                                    <XCircle size={16} /> Denegar
                                </button>}
                            </div>
                        </motion.div>
                    ))
                )}
            </div>
            <div className="admin-pagination"><span>{pagination.count} solicitudes</span><div><button type="button" className="admin-btn admin-btn--outline admin-btn--sm" disabled={page <= 1} onClick={() => setPage(value => value - 1)}>Anterior</button><span>Página {page} de {pagination.pages || 1}</span><button type="button" className="admin-btn admin-btn--outline admin-btn--sm" disabled={page >= (pagination.pages || 1)} onClick={() => setPage(value => value + 1)}>Siguiente</button></div></div>
        </div>

        {/* Reject Modal */}
        <AnimatePresence>
            {showRejectModal && (
                <div className="modal-luxury-overlay" role="presentation" onClick={() => setShowRejectModal(false)}>
                    <motion.div 
                        className="modal-luxury-content" 
                        initial={{ scale: 0.9, opacity: 0 }} 
                        animate={{ scale: 1, opacity: 1 }} 
                        role="dialog" aria-modal="true" aria-labelledby="creator-reject-title" onClick={e => e.stopPropagation()}
                    >
                        <div className="modal-luxury-header">
                            <h3 id="creator-reject-title">Denegar Solicitud</h3>
                            <button type="button" aria-label="Cerrar" className="close-btn-circle" onClick={() => setShowRejectModal(false)}><X size={18}/></button>
                        </div>
                        <form onSubmit={handleReject}>
                            <div className="modal-luxury-body">
                                <p>Explica al creador <strong>{selectedCreator?.user.username}</strong> por qué su solicitud no ha sido aceptada:</p>
                                <textarea 
                                    className="admin-textarea" 
                                    style={{ width: '100%', minHeight: '100px', borderRadius: '12px', padding: '12px', border: '1px solid #d8dee8', marginTop: '12px' }}
                                    placeholder="Ej: La cuenta de Instagram no cumple con los requisitos mínimos de engagement..."
                                    value={rejectionReason}
                                    maxLength={2000}
                                    onChange={e => setRejectionReason(e.target.value)}
                                    required
                                />
                            </div>
                            <div className="modal-luxury-footer">
                                <button className="admin-btn admin-btn--outline" type="button" onClick={() => setShowRejectModal(false)}>Cancelar</button>
                                <button className="admin-btn" style={{ background: '#ff4d5e' }} type="submit" disabled={processing}>Confirmar Denegación</button>
                            </div>
                        </form>
                    </motion.div>
                </div>
            )}
        </AnimatePresence>
    </motion.div>
  );
};

export default CreatorVerifications;
